import type {
  GlobalActiveSeasonView,
  SimulateSessionResponse,
} from '@multirx/shared/types/championship.js';

export function isSimulateSessionResponse(
  value: GlobalActiveSeasonView | SimulateSessionResponse
): value is SimulateSessionResponse {
  return value != null && 'season' in value && 'completedSession' in value;
}

export function isGlobalActiveSeasonView(
  view: GlobalActiveSeasonView | SimulateSessionResponse
): view is GlobalActiveSeasonView {
  if (isSimulateSessionResponse(view)) return false;
  return 'divisions' in view;
}

export type SimulateSessionOutcome = 'skipped-in-flight' | 'cancelled' | 'success' | 'error';

export type RunSimulateSessionDeps = {
  confirm: (message: string) => boolean | Promise<boolean>;
  simulate: (sessionId: number) => Promise<SimulateSessionResponse>;
  onResults?: (response: SimulateSessionResponse) => void | Promise<void>;
  paintGlobalView: (view: GlobalActiveSeasonView) => void | Promise<void>;
  onError: (error: unknown) => void;
  isInFlight: () => boolean;
  setInFlight: (value: boolean) => void;
};

export function buildSimulateConfirmMessage(divisionLabel: string, sessionLabel: string): string {
  return `Simuler ${divisionLabel} — ${sessionLabel} ?\n\nLa course sera jouée en arrière-plan et son replay sera enregistré.`;
}

/** Logique pure de simulation (testable sans DOM). */
export async function runSimulateSessionWithDeps(
  sessionId: number,
  divisionLabel: string,
  sessionLabel: string,
  deps: RunSimulateSessionDeps
): Promise<SimulateSessionOutcome> {
  if (deps.isInFlight()) {
    return 'skipped-in-flight';
  }

  // Verrouiller dès l’ouverture de la confirmation — sinon un second clic
  // empile des modales « Simuler la session » (isInFlight encore false).
  deps.setInFlight(true);
  try {
    if (!(await deps.confirm(buildSimulateConfirmMessage(divisionLabel, sessionLabel)))) {
      return 'cancelled';
    }

    const response = await deps.simulate(sessionId);
    if (!isSimulateSessionResponse(response)) {
      throw new Error('Réponse saison invalide après simulation');
    }
    await deps.onResults?.(response);
    await deps.paintGlobalView(response.season);
    return 'success';
  } catch (error) {
    deps.onError(error);
    return 'error';
  } finally {
    deps.setInFlight(false);
  }
}
